<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useProductPage } from '../../../business/use-product-page';
import ProductTab from './product-tab.vue';
import Characteristics from '../../desktop/characteristics.vue';
import type { ICharacteristics } from '../../../models/characteristic';
import ProductTabCharacteristics from './product-tab-characteristics.vue';
import ProductTabsReviews from '../tabs-reviews/product-tabs-reviews.vue';
import { FeatureFlags } from '@/features/feature-flags';
import { AnalyticAPI } from '../../../business/analytics';
import { useOtherReviewPopup } from '~/features/product-page/features';
import { MMText } from '@mm-frontend/mm-web-ui-kit';
// Get product page state
const { state, setCurrentTabSlug } = useProductPage();

type Tab = { value: string; type: string; count?: number };

const DEFAULT_TAB_SLUGS = {
  description: 'Описание',
  reviews: 'Отзывы',
  characteristics: 'Характеристики',
} as const;

const currentTabSlug = computed(() => {
  return state.currentTabSlug || DEFAULT_TAB_SLUGS.description;
});

const feedbackQuantity = computed(() => {
  return state.product?.feedbackQuantity || 0;
});

const feedbackQuantityForTabs = computed(() => {
  return state.product?.externalRating ? 0 : feedbackQuantity.value;
});

const characteristics = computed(() => {
  return state.characteristics;
});

const tabs = computed((): Tab[] => {
  const description = state.product?.description || state.product?.shortDescription;
  let descriptionTab: Tab | null = null;
  let reviewsTab: Tab = {
    value: '',
    type: DEFAULT_TAB_SLUGS.reviews,
    count: feedbackQuantityForTabs.value,
  };
  let characteristicsTab: Tab | null = null;

  // Описание - всегда первый таб, если есть
  if (description) {
    descriptionTab = { value: description, type: DEFAULT_TAB_SLUGS.description };
  }

  // Характеристики - если есть данные
  if (characteristics.value && characteristics.value.list[0]?.values.length) {
    characteristicsTab = { value: '', type: DEFAULT_TAB_SLUGS.characteristics };
  }

  // Табы из GraphQL (comments) - между описанием и характеристиками
  const stateTabs = state.tabs || [];

  // Порядок: Описание -> Табы из GraphQL -> Характеристики -> Отзывы
  return [descriptionTab, ...stateTabs, characteristicsTab, reviewsTab].filter(Boolean) as Tab[];
});

const currentTab = computed(() => {
  return tabs.value.find((tab) => tab.type === currentTabSlug.value);
});

const productId = computed(() => {
  return state.product?.id;
});

const tabsValueForAnalyticks = [
  {
    name: 'Описание',
    key: 'PRODUCT_DESCRIPTION',
  },
  {
    name: 'Состав',
    key: 'PRODUCT_COMPOSITION_DETAILS',
  },
  {
    name: 'Размеры',
    key: 'PRODUCT_SIZE_DETAILS',
  },
  {
    name: 'Инструкция',
    key: 'PRODUCT_INSTRUCTION',
  },
  {
    name: 'Сертификация',
    key: 'PRODUCT_CERTIFICATION',
  },
  {
    name: 'Характеристики',
    key: 'PRODUCT_CHARACTERISTIC',
  },
  {
    name: 'Отзывы',
    key: 'PRODUCT_REVIEWS',
  },
];

const onTabChange = (slug: string) => {
  analyticsQuery(slug);
  setCurrentTabSlug(slug);
  showPopupExternalRating(slug);
};

const { showOtherReviewPopup } = useOtherReviewPopup();
const showPopupExternalRating = (slug: string) => {
  if (slug === 'Отзывы' && state.product?.externalRating) {
    showOtherReviewPopup({
      rating: state.product.rating,
      reviews: state.product.feedbackQuantity,
    });
  }
};

const analyticsQuery = (data: string) => {
  const currentSku = state.currentSku;
  if (!currentSku) return;

  AnalyticAPI.sendAnalyticsChangeTabs({
    quantity: currentSku.availableAmount,
    sell_price: currentSku.purchasePrice,
    sku_id: currentSku.id,
    // is_vpn_enabled: VpnDetector.isDetectedValue(),
    widget: tabsValueForAnalyticks.find((item) => item.name === data)?.key || '',
  });
};

onMounted(async () => {
  // Feature flag check removed - always use new reviews component
});
</script>

<template>
  <div id="description-tabs-wrapper" class="product-tabs" data-test-id="block__product-info-tabs">
    <div class="product-tabs-wrap">
      <product-tab
        v-for="tab in tabs"
        :key="tab.type"
        :active="currentTabSlug === tab.type"
        :title="tab.type"
        :count="tab.count"
        :slug="tab.type"
        data-test-id="button__product-info-tab"
        @click="onTabChange"
      />
    </div>
    <keep-alive>
      <template v-if="currentTabSlug === DEFAULT_TAB_SLUGS.reviews">
        <product-tabs-reviews :key="`${productId}-reviews-new`" class="reviews" />
      </template>
      <product-tab-characteristics
        v-else-if="currentTabSlug === DEFAULT_TAB_SLUGS.characteristics && characteristics"
        :key="`${productId}-characteristics`"
        :characteristics="characteristics as ICharacteristics"
      />
      <MMText
        font="Body L/Regular Long"
        v-else-if="currentTab"
        class="product-tabs-content tab-content editor"
        v-html="currentTab.value"
      />
    </keep-alive>
    <div class="product-tabs-all-content" hidden>
      <template v-for="tab in tabs" :key="tab.value">
        <div v-html="tab.value" />
      </template>
    </div>
  </div>
</template>

<style scoped lang="stylus">
.product-tabs
  width 100%
  overflow hidden

.product-tabs-wrap
  display flex
  align-items: flex-start
  flex-wrap wrap
  gap 16px
  margin-top 8px
  margin-bottom 24px
</style>

<style src="./editor.css" />
